import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col, Form, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Pagination, PaginationItem, PaginationLink, Badge, Nav, NavItem, NavLink } from 'reactstrap';
import ApiService from '../services/ApiService';
import { useThemeContext } from '../contexts/ThemeContext';

const DetectionReviewPanel = () => {
  const [tasks, setTasks] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sources, setSources] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [isLoadingDetections, setIsLoadingDetections] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    tenant_id: '',
    brand_id: '',
    source_type: '',
    source_category: '',
    status: '',
    date_range: '',
  });
  const [summary, setSummary] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskDetections, setSelectedTaskDetections] = useState([]);
  const [selectedTaskMediaUrl, setSelectedTaskMediaUrl] = useState('');
  const [originalMediaUrl, setOriginalMediaUrl] = useState('');
  const [activeTab, setActiveTab] = useState('detection');
  const [reviewModal, setReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    status: '',
    comments: ''
  });
  const { isDark } = useThemeContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hoveredTrack, setHoveredTrack] = useState(null);
  const videoRef = React.useRef(null);
  const [isVideo, setIsVideo] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [originalImageSize, setOriginalImageSize] = useState({ width: 0, height: 0 });
  const imageRef = React.useRef(null);
  const originalImageRef = React.useRef(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [currentPage, filters]);

  useEffect(() => {
    let cancelled = false;
    ApiService.getDetectionTaskSummary()
      .then((res) => { if (!cancelled) setSummary(res.data); })
      .catch((err) => { console.error('Error fetching detection task summary:', err); });
    return () => { cancelled = true; };
  }, []);

  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageSize({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight
      });
    }
  };

  const handleOriginalImageLoad = () => {
    if (originalImageRef.current) {
      setOriginalImageSize({
        width: originalImageRef.current.naturalWidth,
        height: originalImageRef.current.naturalHeight
      });
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await ApiService.getDetectionReviewTenants();
      console.log('Tenants API Response:', response);
      // The API returns the array directly in response.data
      const tenantsData = Array.isArray(response.data) ? response.data : [];
      console.log('Processed Tenants Data:', tenantsData);
      setTenants(tenantsData);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError('Failed to load tenants. Please try again later.');
      // Set empty array on error
      setTenants([]);
    }
  };

  const fetchTasks = async () => {
    try {
      setIsLoadingTasks(true);
      setError(null);

      const params = {
        page: currentPage,
        per_page: 12,
        tenant_id: filters.tenant_id,
        brand_id: filters.brand_id,
        source_type: filters.source_type,
        source_category: filters.source_category,
        status: filters.status,
        date_range: filters.date_range,
      };

      const response = await ApiService.getDetectionTasks(params);
      
      console.log('Tasks API Response:', response.data);
      
      const { data: tasksData, current_page, total, per_page } = response.data;
      
      // Enhanced logging to debug media properties
      tasksData.forEach((task, index) => {
        console.log(`Task ${index} (${task.public_id}):`);
        console.log(`- media_preview_url:`, task.media_preview_url);
        console.log(`- media object:`, task.media);
        if (task.media) {
          console.log(`- media.url:`, task.media.url);
        }
      });
      
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setTotalPages(Math.ceil(total / per_page));
      
      const uniqueBrands = [...new Set(tasksData.map(task => task.brand).filter(Boolean))];
      const uniqueSources = [...new Set(tasksData.map(task => task.media?.source_name).filter(Boolean))];
      
      setBrands(uniqueBrands);
      setSources(uniqueSources);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Failed to load tasks. Please try again later.');
      setTasks([]);
      setBrands([]);
      setSources([]);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleReviewClick = async (task) => {
    try {
      if (!task || !task.public_id) {
        setError("Invalid task information");
        return;
      }

      setSelectedTask(task);
      setSelectedTaskDetections([]);
      setSelectedTaskMediaUrl('');
      
      // Set the original media URL from the task's media object if available or from preview_url
      // Make sure we're using the ORIGINAL image URL from the media object, not the preview
      const originalUrl = task.media?.url || task.media_preview_url || '';
      console.log('Setting original media URL:', originalUrl);
      setOriginalMediaUrl(originalUrl);
      
      setIsLoadingDetections(true);
      setError(null);
      setActiveTab('detection');
      setImageSize({ width: 0, height: 0 });
      setOriginalImageSize({ width: 0, height: 0 });
      setReviewModal(true);

      const params = {
        brand_id: filters.brand_id
      };

      const response = await ApiService.getTaskDetections(task.public_id, params);
      
      console.log('Task detections response:', response.data);
      console.log('Detections array:', response.data.detections);
      console.log('Detections count:', Array.isArray(response.data.detections) ? response.data.detections.length : 'Not an array');
      
      // The detections array is directly in response.data.detections, not nested
      setSelectedTaskDetections(response.data.detections || []);
      
      // Set the detection media URL from the API response
      const detectionUrl = response.data.media_url || '';
      console.log('Setting detection media URL:', detectionUrl);
      setSelectedTaskMediaUrl(detectionUrl);
      
      // If we got a better original URL from the API, update it
      if (response.data.media?.url) {
        console.log('Updating original URL from API response:', response.data.media.url);
        setOriginalMediaUrl(response.data.media.url);
      }
      
      // Update selected task with additional media info if available
      if (response.data.media) {
        setSelectedTask(prevTask => ({
          ...prevTask,
          media: {
            ...prevTask.media,
            ...response.data.media
          }
        }));
      }
      
      // Set review data if available
      if (response.data.review) {
        setReviewData({
          status: response.data.review.status,
          comments: response.data.review.comments || ''
        });
      } else {
        // Reset review data if no review exists
        setReviewData({
          status: 'not_reviewed',
          comments: ''
        });
      }
      
      setIsLoadingDetections(false);
    } catch (err) {
      console.error('Error fetching detections:', err);
      setError('Failed to load detection data. Please try again.');
      setIsLoadingDetections(false);
    }
  };

  const handleReviewSubmit = async () => {
    if (!selectedTask) return;
    
    try {
      await ApiService.createDetectionReview(selectedTask.public_id, {
        status: reviewData.status,
        comments: reviewData.comments
      });
      
      const updatedTask = {
        ...selectedTask,
        review: {
          status: reviewData.status,
          comments: reviewData.comments,
          is_approved: reviewData.status === 'approved',
          is_rejected: reviewData.status === 'rejected',
          created_at: new Date().toISOString()
        }
      };
      setSelectedTask(updatedTask);
      
      // Close the modal before refreshing tasks
      setReviewModal(false);
      setSelectedTaskDetections([]);
      setSelectedTaskMediaUrl('');
      setOriginalMediaUrl('');
      setActiveTab('detection');
      setImageSize({ width: 0, height: 0 });
      setOriginalImageSize({ width: 0, height: 0 });
      setReviewData({
        status: '',
        comments: ''
      });
      
      // If there's an active status filter that would exclude this task after review,
      // don't update the local tasks state, just fetch fresh data
      if (filters.status === 'not_reviewed' || 
          (filters.status === 'approved' && reviewData.status !== 'approved') ||
          (filters.status === 'rejected' && reviewData.status !== 'rejected')) {
        // Just refresh the task list from the API since this task should be filtered out
        fetchTasks();
      } else {
        // Update the local state first for immediate feedback
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.public_id === selectedTask.public_id ? updatedTask : task
          )
        );
        
        // Then fetch fresh data to ensure everything is in sync
        fetchTasks();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      setError('Failed to submit review. Please try again.');
    }
  };

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const groupDetectionsByTrack = (detections) => {
    console.log('Grouping detections by track:', detections);
    
    if (!Array.isArray(detections) || detections.length === 0) {
      console.log('No detections to group or invalid data format');
      return [];
    }
    
    // Check if this is image or video by looking at inference type or checking if all track_ids are 0
    const isImageDetection = detections[0]?.inference === 'image' || 
                             (detections.every(d => d.track_id === 0) && detections.length > 0);
    
    console.log(`Detection type: ${isImageDetection ? 'Image' : 'Video'}`);
    
    const tracks = {};
    detections.forEach(detection => {
      // Skip invalid detections
      if (!detection || typeof detection.track_id !== 'number') {
        console.log('Invalid detection found, skipping:', detection);
        return;
      }
      
      let trackKey;
      
      if (isImageDetection) {
        // For images, treat each logo_class as a separate "track"
        // This ensures we get separate cards for each brand detected
        trackKey = detection.logo_class || 'unknown';
      } else {
        // For videos, use the actual track_id which represents the same object across frames
        trackKey = `track-${detection.track_id}`;
      }
      
      if (!tracks[trackKey]) {
        tracks[trackKey] = {
          track_id: detection.track_id,
          logo_class: detection.logo_class,
          detections: [],
          min_frame: Infinity,
          max_frame: -Infinity,
          avg_confidence: 0,
          inference_type: detection.inference || 'unknown'
        };
      }
      
      tracks[trackKey].detections.push(detection);
      tracks[trackKey].min_frame = Math.min(tracks[trackKey].min_frame, detection.frame_number || 0);
      tracks[trackKey].max_frame = Math.max(tracks[trackKey].max_frame, detection.frame_number || 0);
    });

    // Calculate average confidence for each track
    Object.values(tracks).forEach(track => {
      track.avg_confidence = track.detections.reduce((sum, d) => sum + (d.confidence || 0), 0) / track.detections.length;
    });

    const result = Object.values(tracks);
    console.log('Grouped tracks:', result);
    return result;
  };

  const handleTrackHover = (track) => {
    setHoveredTrack(track);
    
    // For video content, seek to the middle frame
    if (videoRef.current && isVideo && track.min_frame !== track.max_frame) {
      // Calculate the frame to seek to (middle of the track)
      const middleFrame = Math.floor((track.min_frame + track.max_frame) / 2);
      // Assuming 30fps video
      const timeToSeek = middleFrame / 30;
      videoRef.current.currentTime = timeToSeek;
    }
    
    // Log which detection is being hovered for debugging
    if (track && track.detections && track.detections.length > 0) {
      console.log('Hovering detection:', track.detections[0]);
      if (track.detections[0].bbox_points) {
        console.log('Bounding box:', track.detections[0].bbox_points);
      }
    }
  };

  const handleTrackLeave = () => {
    setHoveredTrack(null);
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      console.log(`Switching tab from ${activeTab} to ${tab}`);
      
      if (tab === 'detection') {
        console.log('Detection tab URL:', selectedTaskMediaUrl);
      } else {
        console.log('Original tab URL:', originalMediaUrl);
      }
      
      // Check if URLs are the same
      if (selectedTaskMediaUrl === originalMediaUrl) {
        console.log('Warning: Detection and Original URLs are identical!');
      }
      
      setActiveTab(tab);
    }
  };

  const renderMediaPreview = (url, isModal = false, isOriginal = false, key = '') => {
    if (!url || typeof url !== 'string') {
      return (
        <div 
          style={{ 
            width: '100%', 
            height: isModal ? '300px' : '150px', 
            backgroundColor: '#f0f0f0', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#888'
          }}
        >
          No Preview Available
        </div>
      );
    }

    console.log(`[renderMediaPreview] URL: ${url}, isModal: ${isModal}, isOriginal: ${isOriginal}`);
    
    // Add a cache-busting query parameter when needed
    // This ensures that when we switch tabs, the browser doesn't show a cached version
    let mediaUrl = url;
    
    // If we're in modal and the URLs for detection and original are the same,
    // add a parameter to force browser to treat them as different resources
    if (isModal && selectedTaskMediaUrl === originalMediaUrl) {
      const separator = mediaUrl.includes('?') ? '&' : '?';
      mediaUrl = `${mediaUrl}${separator}view=${isOriginal ? 'original' : 'detection'}&t=${Date.now()}`;
      console.log(`Adding cache-busting to URL: ${mediaUrl}`);
    }
    
    // Improved video detection - look for .mp4 or .mov anywhere in the URL, not just at the end
    // This handles cases where the extension is in the middle of a URL or part of a query parameter
    const isVideoFile = mediaUrl.includes('.mp4') || mediaUrl.includes('.mov');
    
    console.log(`[renderMediaPreview] Is video: ${isVideoFile}`);
    
    const style = isModal 
      ? { width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }
      : { width: '100%', height: 'auto', maxHeight: '150px', objectFit: 'cover' };

    if (isVideoFile) {
      return (
        <video 
          ref={videoRef}
          src={mediaUrl}
          style={style}
          controls
          playsInline
          title="Media Preview"
          className={isModal ? 'w-100' : ''}
          key={key}
        />
      );
    }

    return (
      <img 
        src={mediaUrl} 
        alt="Media Preview" 
        style={style}
        onLoad={isModal ? (isOriginal ? handleOriginalImageLoad : handleImageLoad) : undefined}
        ref={isModal ? (isOriginal ? originalImageRef : imageRef) : undefined}
        key={key}
      />
    );
  };

  // Update video state when selected task changes
  useEffect(() => {
    if (selectedTaskMediaUrl) {
      // Use same improved video detection as in renderMediaPreview
      setIsVideo(selectedTaskMediaUrl.includes('.mp4') || selectedTaskMediaUrl.includes('.mov'));
    }
  }, [selectedTaskMediaUrl]);

  // When switching tabs, reset the relevant image size
  useEffect(() => {
    if (activeTab === 'detection') {
      setOriginalImageSize({ width: 0, height: 0 });
    } else {
      setImageSize({ width: 0, height: 0 });
    }
  }, [activeTab]);

  const handleModalToggle = () => {
    setReviewModal(!reviewModal);
    if (reviewModal) {
      // When closing the modal, reset to detection tab
      setActiveTab('detection');
      setImageSize({ width: 0, height: 0 });
      setOriginalImageSize({ width: 0, height: 0 });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format date in DD/MM/YY format for the task cards
  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().substring(2, 4);
    return `${day}/${month}/${year}`;
  };

  // Get the best available date for a media item
  const getMediaPublishedDate = (task) => {
    // Make sure task exists
    if (!task) {
      return 'N/A';
    }
    
    // First try to use published_at if available
    if (task.media?.published_at) {
      return formatDate(task.media.published_at);
    }
    
    // If not available, use media created_at as fallback with a note
    if (task.media?.created_at) {
      return `${formatDate(task.media.created_at)} (indexed)`;
    }
    
    // Last resort, use the task created_at
    return task.created_at ? `${formatDate(task.created_at)} (task created)` : 'N/A';
  };

  // Get friendly source type description
  const getSourceDescription = (task) => {
    // Make sure task exists
    if (!task) {
      return 'Unknown Source';
    }
    
    if (!task.media) return 'Unknown Source';
    
    const sourceName = task.media.source_name;
    const sourceId = task.media.source_id;
    
    switch (sourceName) {
      case 'instagram':
      case 'instagram-post':
      case 'instagram-album-item':
        return `Instagram Post (ID: ${sourceId || 'Unknown'})`;
      case 'facebook':
      case 'facebook-post':
        return `Facebook Post (ID: ${sourceId || 'Unknown'})`;
      case 'meltwater-mention-image':
        return `Meltwater Mention (ID: ${sourceId || 'Unknown'})`;
      default:
        return `${sourceName || 'Unknown'} (ID: ${sourceId || 'N/A'})`;
    }
  };

  // Format the source name for compact display
  const formatSourceName = (sourceName) => {
    if (!sourceName) return { main: 'Unknown', detail: '' };
    
    if (sourceName.includes('instagram')) {
      return { main: 'Instagram', detail: sourceName.includes('post') ? ' post' : sourceName.includes('album') ? ' album' : '' };
    }
    
    if (sourceName.includes('facebook')) {
      return { main: 'Facebook', detail: sourceName.includes('post') ? ' post' : '' };
    }
    
    if (sourceName.includes('meltwater')) {
      return { main: 'Meltwater', detail: ' mention' };
    }
    
    return { main: sourceName, detail: '' };
  };

  // Function to handle approving with no detections
  const handleApproveNoDetections = async () => {
    console.log("Approving with no detections");
    
    if (!selectedTask) {
      console.error("No task selected");
      return;
    }
    
    try {
      console.log("Selected task for approval:", selectedTask.public_id);
      
      // Call API directly without using the form state
      const response = await ApiService.createDetectionReview(selectedTask.public_id, {
        status: 'approved',
        comments: 'No detections'
      });
      
      console.log("API response:", response);
      
      const updatedTask = {
        ...selectedTask,
        review: {
          status: 'approved',
          comments: 'No detections',
          is_approved: true,
          is_rejected: false,
          created_at: new Date().toISOString()
        }
      };
      
      setSelectedTask(updatedTask);
      
      // Close the modal
      setReviewModal(false);
      setSelectedTaskDetections([]);
      setSelectedTaskMediaUrl('');
      setOriginalMediaUrl('');
      setActiveTab('detection');
      setImageSize({ width: 0, height: 0 });
      setOriginalImageSize({ width: 0, height: 0 });
      
      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.public_id === selectedTask.public_id ? updatedTask : task
        )
      );
      
      // Refresh data
      setTimeout(() => {
        fetchTasks();
      }, 300);
      
    } catch (error) {
      console.error('Error approving with no detections:', error);
      setError('Failed to approve. Please try again.');
    }
  };

  if (isLoadingTasks && currentPage === 1) {
    return (
      <div className="container-fluid pt-5 p-0">
        <Row className="mb-4">
          <Col xs={12} className="text-center">
            <Spinner color="primary" />
            <p>Loading review tasks...</p>
          </Col>
        </Row>
      </div>
    );
  }

  const renderStatusSummary = () => {
    if (!summary) return null;
    const windows = summary.windows || {};
    const failures = summary.recent_failures || [];
    const statuses = ['pending', 'processing', 'completed', 'failed'];
    const statusColor = {
      pending: 'secondary',
      processing: 'info',
      completed: 'success',
      failed: 'danger',
    };
    return (
      <Card className={`admin-card mb-3 ${isDark ? 'dark' : ''}`}>
        <CardBody>
          <h5 className="mb-3">Detection Task Status</h5>
          <Row>
            {['24h', '7d'].map((win) => {
              const w = windows[win] || { by_status: {}, by_source: {} };
              return (
                <Col md={6} key={win}>
                  <div className="mb-2">
                    <strong>Last {win}</strong>
                  </div>
                  <div className="d-flex flex-wrap mb-2" style={{ gap: '0.5rem' }}>
                    {statuses.map((s) => (
                      <Badge key={s} color={statusColor[s]} pill>
                        {s}: {w.by_status[s] || 0}
                      </Badge>
                    ))}
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                    {Object.entries(w.by_source).map(([src, cnt]) => (
                      <Badge key={src} color="light" pill style={{ color: '#333' }}>
                        {src}: {cnt}
                      </Badge>
                    ))}
                  </div>
                </Col>
              );
            })}
          </Row>
          {failures.length > 0 && (
            <div className="mt-3">
              <strong>Recent failures (7d):</strong>
              <ul className="mb-0" style={{ paddingLeft: '1.25rem' }}>
                {failures.slice(0, 10).map((f) => (
                  <li key={f.public_id}>
                    <small>
                      <code>{f.public_id}</code> — {f.source} — {formatShortDate(f.created_at)}
                    </small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <div className="container-fluid pt-5 p-0">
      <Row className="mb-4">
        <Col xs={12}>
          {renderStatusSummary()}
          <Card className={`admin-card ${isDark ? 'dark' : ''}`}>
            <CardBody>
              <Form className="mb-4">
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="tenant_id">Tenant</Label>
                      <Input
                        type="select"
                        name="tenant_id"
                        id="tenant_id"
                        value={filters.tenant_id}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Tenants</option>
                        {tenants.map(tenant => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label for="source_type">Source Type</Label>
                      <Input
                        type="select"
                        name="source_type"
                        id="source_type"
                        value={filters.source_type}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Sources</option>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="meltwater">Meltwater</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label for="source_category">Category</Label>
                      <Input
                        type="select"
                        name="source_category"
                        id="source_category"
                        value={filters.source_category}
                        onChange={handleFilterChange}
                        disabled={!filters.tenant_id}
                        title={!filters.tenant_id ? 'Select a tenant first' : ''}
                      >
                        <option value="">All Categories</option>
                        <option value="earned">Earned</option>
                        <option value="owned">Owned</option>
                        {(() => {
                          const selectedTenant = tenants.find(t => String(t.id) === String(filters.tenant_id));
                          if (selectedTenant?.features?.['tracked-accounts']) {
                            return <option value="tracked">Tracked</option>;
                          }
                          return null;
                        })()}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>Status</Label>
                      <Input
                        type="select"
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Statuses</option>
                        <option value="not_reviewed">Not Reviewed</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3} lg={3}>
                    <FormGroup>
                      <Label>Publication Date</Label>
                      <Input
                        type="select"
                        name="date_range"
                        value={filters.date_range}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Dates</option>
                        <option value="today">Published Today</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="this_month">This Month</option>
                        <option value="last_month">Last Month</option>
                        <option value="this_year">This Year</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              </Form>

              {isLoadingTasks ? (
                <div className="text-center"><Spinner size="sm" /> Loading tasks...</div>
              ) : error ? (
                <div className="alert alert-danger">{error} <Button size="sm" color="link" onClick={fetchTasks}>Retry</Button></div>
              ) : tasks.length === 0 ? (
                <div className="text-center"><p>No tasks found.</p></div>
              ) : (
                <div className="task-list">
                  <h5>Tasks for Review</h5>
                  <Row xs="1" sm="2" md="3" lg="4">
                    {tasks.map((task) => (
                      <Col key={task.public_id} className="mb-3">
                        <Card 
                          className={`h-100 ${selectedTask?.public_id === task.public_id ? 'border-primary' : ''}`}
                          onClick={() => handleReviewClick(task)}
                          style={{ cursor: 'pointer' }}
                        >
                          <CardBody className="p-2">
                            <div className="media-preview mb-2 position-relative">
                              {/* 
                                Now that DetectionReviewService is generating proper signed URLs,
                                we can use media_preview_url for all media types
                              */}
                              {renderMediaPreview(task.media_preview_url, false)}
                              
                              {/* Review status indicator badge */}
                              {task.review && (
                                <Badge 
                                  color={task.review.status === 'approved' ? 'success' : task.review.status === 'rejected' ? 'danger' : 'warning'} 
                                  className="position-absolute review-status-badge"
                                  title={task.review.status === 'approved' ? 'Approved' : task.review.status === 'rejected' ? 'Rejected' : 'Pending'}
                                >
                                  {task.review.status === 'approved' ? '✓' : task.review.status === 'rejected' ? '✗' : '?'}
                                </Badge>
                              )}
                            </div>
                            
                            <div className="task-info">
                              <div className="date-info">
                                <div className="d-flex justify-content-between">
                                
                                  <small className="text-muted">
                                    <strong>Detected:</strong> {formatShortDate(task.created_at)}
                                  </small>
                                  <small className="text-muted">
                                    <strong>Published:</strong> {formatShortDate(task.media?.published_at)}
                                  </small>
                                </div>
                              </div>
                              
                              <div className="mt-2 d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  <i className={`fa fa-${task.media?.source_name?.includes('instagram') ? 'instagram' :
                                    task.media?.source_name?.includes('facebook') ? 'facebook' :
                                    task.media?.source_name?.includes('meltwater') ? 'newspaper' : 'image'}`}
                                    title={getSourceDescription(task)}></i>
                                  {task.source_category && (
                                    <Badge
                                      color={task.source_category === 'tracked' ? 'warning' : task.source_category === 'owned' ? 'info' : 'secondary'}
                                      pill
                                      className="ml-1"
                                      title={`Source category: ${task.source_category}`}
                                    >
                                      {task.source_category}
                                    </Badge>
                                  )}
                                  <span className="ml-1"><strong>ID:</strong> {task.public_id}</span>
                                </small>

                                <Button 
                                  color="info" 
                                  size="sm"
                                  className="review-btn"
                                  disabled={isLoadingDetections && selectedTask?.public_id === task.public_id}
                                >
                                  {isLoadingDetections && selectedTask?.public_id === task.public_id ? <Spinner size="sm" /> : 'Review'}
                                </Button>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {totalPages > 1 && (
                <Pagination className="mt-4 justify-content-center">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PaginationItem key={`page-${page}`} active={page === currentPage}>
                      <PaginationLink onClick={() => handlePageChange(page)}>
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                </Pagination>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={reviewModal} toggle={handleModalToggle} size="lg">
        <ModalHeader toggle={handleModalToggle}>
          Review Detections for Task {selectedTask?.public_id}
        </ModalHeader>
        <ModalBody>
          {isLoadingDetections ? (
            <div className="text-center">
              <Spinner color="primary" />
              <p>Loading detections...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              <div className="mb-4 position-relative">
                <h5>Media Preview</h5>
                <Nav tabs className="mb-3">
                  <NavItem>
                    <NavLink
                      className={activeTab === 'detection' ? 'active' : ''}
                      onClick={() => toggleTab('detection')}
                      style={{ cursor: 'pointer' }}
                    >
                      Detection
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'original' ? 'active' : ''}
                      onClick={() => toggleTab('original')}
                      style={{ cursor: 'pointer' }}
                    >
                      Original
                      {selectedTaskMediaUrl === originalMediaUrl && (
                        <small className="ml-1 text-warning">(same as detection)</small>
                      )}
                    </NavLink>
                  </NavItem>
                </Nav>
                <div style={{ position: 'relative' }}>
                  {activeTab === 'detection' ? (
                    <>
                      {renderMediaPreview(selectedTaskMediaUrl, true, false, `detection-${selectedTask?.public_id}`)}
                      {hoveredTrack && hoveredTrack.detections[0]?.bbox_points && imageSize.width > 0 && (
                        <div 
                          style={{
                            position: 'absolute',
                            border: '2px solid #ff0000',
                            backgroundColor: 'rgba(255, 0, 0, 0.2)',
                            left: `${(Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 0)) / imageSize.width) * 100}%`,
                            top: `${(Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 1)) / imageSize.height) * 100}%`,
                            width: `${(Math.max(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 0)) - Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 0))) / imageSize.width * 100}%`,
                            height: `${(Math.max(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 1)) - Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 1))) / imageSize.height * 100}%`,
                            pointerEvents: 'none',
                            transition: 'all 0.2s ease'
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {renderMediaPreview(originalMediaUrl, true, true, `original-${selectedTask?.public_id}`)}
                      {/* Show the same annotations on the original image */}
                      {hoveredTrack && hoveredTrack.detections[0]?.bbox_points && originalImageSize.width > 0 && (
                        <div 
                          style={{
                            position: 'absolute',
                            border: '2px solid #ff0000',
                            backgroundColor: 'rgba(255, 0, 0, 0.2)',
                            left: `${(Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 0)) / originalImageSize.width) * 100}%`,
                            top: `${(Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 1)) / originalImageSize.height) * 100}%`,
                            width: `${(Math.max(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 0)) - Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 0))) / originalImageSize.width * 100}%`,
                            height: `${(Math.max(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 1)) - Math.min(...hoveredTrack.detections[0].bbox_points.filter((_, i) => i % 2 === 1))) / originalImageSize.height * 100}%`,
                            pointerEvents: 'none',
                            transition: 'all 0.2s ease'
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
                <div className="mt-3">
                  <div className="media-info">
                    {selectedTask ? (
                      <>
                        <p className="mb-1"><strong>Source:</strong> {getSourceDescription(selectedTask)}</p>
                        <p className="mb-1"><strong>Published At:</strong> {getMediaPublishedDate(selectedTask)}</p>
                        <p className="mb-1"><strong>Source ID:</strong> {selectedTask?.media?.source_id || 'N/A'}</p>
                      </>
                    ) : (
                      <p>Loading task information...</p>
                    )}
                  </div>
                </div>
              </div>
              
              <h5>Detections</h5>
              {selectedTaskDetections.length === 0 ? (
                <p>No detections found for this task.</p>
              ) : (
                <div className="detections-list">
                  <Row>
                    {groupDetectionsByTrack(selectedTaskDetections).map((track) => (
                      <Col key={`detection-${track.logo_class || track.track_id}`} xs={12} sm={6} md={4} className="mb-3">
                        <Card 
                          className="h-100"
                          onMouseEnter={() => handleTrackHover(track)}
                          onMouseLeave={handleTrackLeave}
                          style={{ cursor: 'pointer' }}
                        >
                          <CardBody className="p-2">
                            {/* Brand/Logo Name */}
                            <h6 className="mb-2">{track.logo_class 
                              ? track.logo_class.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                              : 'Unknown Brand'}
                            </h6>
                            
                            {/* Confidence Level */}
                            <p className="mb-1">
                              <strong>Confidence:</strong> {(track.avg_confidence * 100).toFixed(2)}%
                            </p>
                            
                            {/* For videos, show frame range */}
                            {track.inference_type === 'video' && track.min_frame !== track.max_frame && (
                              <p className="mb-1">
                                <strong>Frames:</strong> {track.min_frame} - {track.max_frame}
                              </p>
                            )}
                            
                            {/* Detection count */}
                            {track.detections.length > 1 && (
                              <p className="mb-1">
                                <strong>Detections:</strong> {track.detections.length}
                              </p>
                            )}
                            
                            {/* Track ID for videos (hidden for images since it's always 0) */}
                            {track.inference_type === 'video' && track.track_id !== 0 && (
                              <p className="mb-1">
                                <strong>Track ID:</strong> {track.track_id}
                              </p>
                            )}
                            
                            {/* Area percentage if available */}
                            {track.detections[0]?.area_percentage && (
                              <p className="mb-1">
                                <strong>Area:</strong> {(track.detections[0].area_percentage * 100).toFixed(2)}%
                              </p>
                            )}
                            
                            {/* Size category if available */}
                            {track.detections[0]?.size_category && (
                              <p className="mb-1">
                                <strong>Size:</strong> {track.detections[0].size_category.charAt(0).toUpperCase() + track.detections[0].size_category.slice(1)}
                              </p>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
              
              <div className="review-form mt-4">
                <h5>Review Decision</h5>
                {selectedTask?.review && (
                  <div className="alert alert-info mb-3">
                    <p className="mb-1">
                      <strong>Current Review:</strong> {selectedTask.review.status === 'approved' ? 'Approved' : 'Rejected'}
                    </p>
                    {selectedTask.review.reviewer && (
                      <p className="mb-1">
                        <strong>Reviewed by:</strong> {selectedTask.review.reviewer.name}
                      </p>
                    )}
                    <p className="mb-1">
                      <strong>Reviewed on:</strong> {formatDate(selectedTask.review.created_at)}
                    </p>
                    {selectedTask.review.comments && (
                      <p className="mb-0">
                        <strong>Comments:</strong> {selectedTask.review.comments}
                      </p>
                    )}
                  </div>
                )}
                <Form>
                  <FormGroup>
                    <Label for="status">Status</Label>
                    <Input
                      type="select"
                      name="status"
                      id="status"
                      value={reviewData.status}
                      onChange={handleReviewChange}
                    >
                      <option value="">Select a status...</option>
                      <option value="approved">Approve</option>
                      <option value="rejected">Reject</option>
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label for="comments">Comments</Label>
                    <Input
                      type="textarea"
                      name="comments"
                      id="comments"
                      value={reviewData.comments || ''}
                      onChange={handleReviewChange}
                      placeholder="Add any comments about your review decision..."
                      rows={3}
                    />
                  </FormGroup>
                </Form>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="secondary" onClick={handleModalToggle}>
            Cancel
          </Button>
          <div>
            {selectedTaskDetections.length === 0 && !isLoadingDetections && !error && !selectedTask?.review && (
              <Button 
                color="success" 
                className="mr-2 approve-no-detections-btn"
                onClick={handleApproveNoDetections}
              >
                Approve No Detections
              </Button>
            )}
            <Button 
              color="primary" 
              onClick={handleReviewSubmit}
              disabled={!reviewData.status}
            >
              Submit Review
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DetectionReviewPanel;